import React, { useEffect, useState } from "react";
import { useLang } from "../i18n/LanguageContext";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "team", label: t.nav.team },
    { id: "partners", label: t.nav.partners },
    { id: "contact", label: t.nav.contact },
  ];

  const handleNav = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "py-3 bg-black/60 backdrop-blur-2xl border-b border-white/5"
          : "py-6 bg-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="nav-logo"
          onClick={() => handleNav("home")}
          aria-label="RF KFZ Meisterbetrieb — Start"
          className="flex items-center group"
        >
          <img
            src="/images/rfkfz-logo-white.webp"
            alt="RF KFZ Meisterbetrieb"
            className="h-8 md:h-10 w-auto object-contain opacity-100 group-hover:opacity-80 transition-opacity duration-300"
          />
        </button>

        {/* Center links */}
        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNav(l.id)}
              className="font-mono-tag text-white/70 hover:text-white transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[var(--rf-red)] transition-all duration-500 group-hover:w-full" />
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-4">
          <button
            data-testid="nav-lang-toggle"
            onClick={toggle}
            className="font-mono-tag text-white/80 hover:text-[var(--rf-red)] transition-colors flex items-center gap-1"
          >
            <span className={lang === "de" ? "text-white" : "text-white/40"}>DE</span>
            <span className="text-white/30">/</span>
            <span className={lang === "en" ? "text-white" : "text-white/40"}>EN</span>
          </button>

          <button
            data-testid="nav-cta-contact"
            onClick={() => handleNav("contact")}
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 btn-glow font-mono-tag text-[0.7rem]"
          >
            {t.nav.cta}
            <span className="dot-pulse" />
          </button>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((p) => !p)}
            className="lg:hidden text-white p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden absolute top-full left-0 right-0 glass border-t border-white/5">
          <div className="flex flex-col p-6 gap-4">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="font-display text-2xl text-white text-left tracking-tight"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
